import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function RouteConfig({ roles, onCreateTab, toastMessages, currentMenu }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [refresh, setRefresh] = useState(false);

    // Keep this array identity stable to avoid needless re-renders/refetches downstream
    const removeContextItems = useMemo(() => ['Print', 'Copy'], []);

    // Centralize CRUD route to avoid duplication/typos across callbacks
    const CRUD_ROUTE = '/home/routes/routeConfigCrud';

    //=============================================================================================================
    // openRouteCrud:
    // - Stable (useCallback) action dispatcher used by the table context actions.
    // - Persists 'menu' and 'operation' in global state. For View/Edit/Copy also persists 'RouteLine'.
    // - Validates required row for operations that need it and shows a localized toast on missing selection.
    // - Builds the tab label with i18n and the route description to improve UX.
    // - Using a stable callback prevents MTable from re-rendering/refetching due to prop identity changes.
    //=============================================================================================================
    const openRouteCrud = useCallback(
        (operation, row) => {
            // Persist current menu and operation in global state
            dispatch(M_StateActions.addM_State({ key: 'menu', value: currentMenu }));
            dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

            // For operations that need a selected row/entity data, validate and persist it in global state
            if (['Edit', 'Copy', 'View'].includes(operation)) {
                if (!row) {
                    toastMessages('error', '', t('SelectedLine'));
                    return;
                }
                dispatch(M_StateActions.addM_State({ key: 'RouteLine', value: row }));
            }

            // Build the tab label with i18n and route description to improve UX
            const tabLabel =
                operation === 'Create'
                    ? t('LabelNewRoute')
                    : operation === 'View'
                    ? `${t('LabelViewRoute')} "${row?.Description ?? ''}"`
                    : `${t('LabelEditRoute')} "${row?.Description ?? ''}"`;

            // Invoke the tab creation callback with the CRUD route and built label
            onCreateTab(CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages, currentMenu]
    );

    //=============================================================================================================
    // replaceFunctions:
    // - Memoized Map consumed by MTable to resolve context actions.
    // - Kept stable with useMemo to avoid unnecessary re-renders and duplicate API calls.
    //=============================================================================================================
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ['View', (row) => openRouteCrud('View', row)],
                ['Create', () => openRouteCrud('Create')],
                ['Edit', (row) => openRouteCrud('Edit', row)],
            ]),
        [openRouteCrud]
    );

    return (
        <div className='divGlobalSettings'>
            <div className='subTitleSettings'>
                <div>
                    <div style={{ color: ctx.secondaryColor }}>{t("Routes")}</div>
                    <div className='labelSubtitleSettings'>{t("InfoRoutes")}</div>
                </div>
            </div>
            <MTable // Dynamic table for Routes
                filtersOnTable
                entityName={'Routes'}
                roleName={'Routes'} 
                rolePermissions={roles?.map(vl => vl.Value)}   // User roles for permission control for Routes Module
                removeContextItems={removeContextItems}
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                replaceFunctions={replaceFunctions} // Context actions resolver
                tabsRailWidth={250}
            />
        </div>
    );
}

RouteConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func
};
